import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteType } from '../Router/routeConfig'
import QuickSearchEmptyState from './QuickSearchEmptyState'
import QuickSearchFooter from './QuickSearchFooter'
import QuickSearchResultItem from './QuickSearchResultItem'
import QuickSearchResultsSection from './QuickSearchResultsSection'
import { Noop } from '@/utils'

interface GroupedResults {
  [section: string]: RouteType[]
}

interface QuickSearchNavigationContentProps {
  query: string
  groupedResults: GroupedResults
  onSelect: (result: RouteType) => void
  flatResults: RouteType[]
  selectedIndex: number
}

export default function QuickSearchNavigationContent(
  props: QuickSearchNavigationContentProps
): React.ReactElement {
  const {
    query,
    selectedIndex,
    onSelect,
    flatResults,
    groupedResults,
  } = props
  const { t } = useTranslation()

  const handleSelect = (result: RouteType): Noop => {
    return () => onSelect(result)
  }

  const hasResults = Object.keys(groupedResults).length > 0

  if (!query.trim()) {
    return (
      <QuickSearchEmptyState
        title={t('quickSearchPlaceholder')}
        subtitle={t('quickSearchSubtitle')}
      />
    )
  }

  if (!hasResults) {
    return (
      <QuickSearchEmptyState
        title={t('quickSearchNoResultsFoundForQuery', { query })}
        subtitle={t('trydifferentKeywords')}
      />
    )
  }

  return (
    <>
      <List sx={{ maxHeight: 500, overflow: 'auto', py: 1 }}>
        {Object.entries(groupedResults).map(([section, sectionResults]) => (
          <QuickSearchResultsSection
            key={section}
            title={section}
            results={sectionResults}
            getKey={(item): string => item.path as string}
            renderItem={(item): JSX.Element => (
              <QuickSearchResultItem
                isSelected={flatResults[selectedIndex]?.path === item.path}
                onClick={handleSelect(item)}
                query={query}
                icon={item.icon}
                subsection={item.subsection}
                text={item?.title}
                description={item.description}
              />
            )}
          />
        ))}
      </List>

      <Divider />

      <QuickSearchFooter
        resultCount={flatResults.length}
        keyboardActions={[
          {
            hints: [{ icon: '↑' }, { icon: '↓' }],
            description: 'navigate',
          },
          {
            hints: [{ icon: '↵' }],
            description: 'select',
          },
        ]}
      />
    </>
  )
}
